/**
 * 轻量 fetch-based SSE 连接类
 *
 * 替代原生 EventSource（原生 EventSource 不支持自定义 headers），
 * 使用 fetch + ReadableStream 手动解析 SSE 协议，
 * API 兼容原生 EventSource：onopen / onmessage / onerror / addEventListener / close
 *
 * SSE 协议解析规则：
 *   - 事件以空行（\n\n，兼容 \r\n\r\n）分隔
 *   - data: 行 -> event.data（多行 data 用 \n 连接）
 *   - event: 行 -> 自定义事件名（缺省为 message）
 *
 * @example
 *   import SSEConnection from '@/utils/sse';
 *   const es = new SSEConnection(url, {
 *     headers: { Authorization: 'Bearer xxx' },
 *     fetch: customFetch, // 可选，Tauri 移动端传 tauriFetch
 *   });
 *   es.onopen = () => { ... };
 *   es.onmessage = (e) => { console.log(e.data); };
 *   es.addEventListener('custom_event', (e) => { ... });
 *   es.close();
 */
class SSEConnection {
  /**
   * @param {string} url  SSE 接口地址
   * @param {object} [options]
   * @param {object} [options.headers]  自定义请求头（支持 Authorization 等原生 EventSource 不能带的）
   * @param {string} [options.method='GET']  HTTP 方法
   * @param {number} [options.reconnectInterval=5000]  自动重连间隔（ms），设 0 关闭自动重连
   * @param {number} [options.maxReconnectAttempts=10]  最大重连次数
   * @param {typeof fetch} [options.fetch=global.fetch]  注入 fetch 实现（用于 Tauri 移动端绕过 CORS）
   */
  constructor(url, options = {}) {
    this.url = url;
    this.headers = options.headers || {};
    this.method = options.method || 'GET';
    this.reconnectInterval = options.reconnectInterval ?? 5000;
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 10;
    this._fetch = options.fetch || fetch;
    this.reconnectAttempts = 0;
    this._closed = false;
    this._abortController = null;
    this._reconnectTimer = null;

    // 事件处理器（兼容原生 EventSource）
    this.onopen = null;
    this.onmessage = null;
    this.onerror = null;
    this._listeners = {}; // { eventName: [fn1, fn2, ...] }

    // 异步连接，不阻塞构造函数
    this._connect();
  }

  addEventListener(eventName, handler) {
    if (!this._listeners[eventName]) {
      this._listeners[eventName] = [];
    }
    this._listeners[eventName].push(handler);
  }

  removeEventListener(eventName, handler) {
    if (!this._listeners[eventName]) return;
    this._listeners[eventName] = this._listeners[eventName].filter(fn => fn !== handler);
  }

  /** 关闭连接，停止自动重连 */
  close() {
    this._closed = true;
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  async _connect() {
    if (this._closed) return;

    this._abortController = new AbortController();

    try {
      const response = await this._fetch(this.url, {
        method: this.method,
        headers: this.headers,
        signal: this._abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE HTTP ${response.status}: ${response.statusText}`);
      }

      // 连接成功，重置重连计数
      this.reconnectAttempts = 0;
      this._emit('open');
      if (typeof this.onopen === 'function') {
        try { this.onopen(); } catch (e) { /* 用户 handler 异常不影响连接 */ }
      }

      if (!response.body || !response.body.getReader) {
        throw new Error('浏览器不支持 ReadableStream');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // 规范化 CRLF -> LF，避免 \r\n\r\n 分隔符漏检
        buffer = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

        // 按 \n\n 分割事件块
        let sepIndex;
        while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
          const chunk = buffer.substring(0, sepIndex);
          buffer = buffer.substring(sepIndex + 2);
          this._processEventBlock(chunk);
        }
      }

      // 处理剩余 buffer 中的数据
      if (buffer.trim()) {
        this._processEventBlock(buffer);
      }

      // 连接正常结束，若未主动关闭则尝试重连
      if (!this._closed) {
        this._scheduleReconnect();
      }
    } catch (err) {
      if (this._closed || err.name === 'AbortError') return;

      // 触发错误事件
      if (typeof this.onerror === 'function') {
        try { this.onerror(err); } catch (e) { /* ignore */ }
      }

      if (this.reconnectInterval > 0 && this.reconnectAttempts < this.maxReconnectAttempts) {
        this._scheduleReconnect();
      } else {
        this._emit('error', err);
      }
    }
  }

  /** 处理一个 SSE 事件块（不含尾部 \n\n，buffer 已提前做过 CRLF 规范化） */
  _processEventBlock(block) {
    let eventName = ''; // 空字符串 = 默认 message
    const dataLines = [];

    const lines = block.split('\n');

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
      // 其他行（注释以 : 开头、空行等）忽略
    }

    if (dataLines.length === 0) return; // 无数据不派发

    const data = dataLines.join('\n');
    const eventObj = { data, type: eventName || 'message' };

    if (eventName === '' || eventName === 'message') {
      // 默认 message 事件
      if (typeof this.onmessage === 'function') {
        try { this.onmessage(eventObj); } catch (e) { /* ignore */ }
      }
    } else {
      // 自定义事件
      this._emit(eventName, eventObj);
    }
  }

  _emit(eventName, payload) {
    const handlers = this._listeners[eventName];
    if (!handlers) return;
    for (const fn of handlers) {
      try { fn(payload); } catch (e) { /* 用户 handler 异常不影响其他 */ }
    }
  }

  _scheduleReconnect() {
    this.reconnectAttempts++;
    if (this._closed) return;
    this._reconnectTimer = setTimeout(() => {
      if (!this._closed) this._connect();
    }, this.reconnectInterval);
  }
}

export default SSEConnection;
